if (!customElements.get("countdown-timer")) {
  customElements.define(
    "countdown-timer",
    class CountdownTimer extends HTMLElement {
      endDate = null;
      rafId = null;
      lastShownSecond = null;
      
      connectedCallback() {
        this.days = this.querySelector("[data-days]");
        this.hrs = this.querySelector("[data-hrs]");
        this.mins = this.querySelector("[data-mins]");
        this.secs = this.querySelector("[data-secs]");

        if (!this.days || !this.hrs || !this.mins || !this.secs) {
          console.error(
            "[Countdown Timer] Missing [data-days]/[data-hrs]/[data-mins]/[data-secs] nodes."
          );
          return;
        }

        // OVERRIDE any external/manual end date:
        this.endDate = this.computeEndDate();

        this.tick();
      }

      disconnectedCallback() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
      }

      tick = () => {
        const now = new Date();
        const nowMs = now.getTime();

        // If we’ve passed the end date, immediately roll over to the next target.
        // This makes the reset happen right after Friday 23:59:59 (i.e. at Saturday 00:00:00).
        while (this.endDate && nowMs > this.endDate.getTime()) {
          this.endDate = this.computeEndDate();
          this.lastShownSecond = null;
        }

        if (!this.endDate || isNaN(this.endDate.getTime())) {
          console.error("[Countdown Timer] Could not compute a valid end date.");
          return;
        }

        const remainingMs = this.endDate.getTime() - nowMs;

        // Hide only if you want it hidden after end; with auto-rollover it will basically never stay ended.
        this.style.display = "block";

        // Update DOM only when the displayed second changes
        const secondsLeft = Math.floor(Math.max(0, remainingMs) / 1000);
        if (secondsLeft !== this.lastShownSecond) {
          this.lastShownSecond = secondsLeft;
          this.updateDisplay(this.getTimeRemaining(remainingMs));
        }

        this.rafId = requestAnimationFrame(this.tick);
      };

      getTimeRemaining(remainingMs) {
        const total = Math.max(0, remainingMs);
        const seconds = Math.floor((total / 1000) % 60);
        const minutes = Math.floor((total / (1000 * 60)) % 60);
        const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
        const days = Math.floor(total / (1000 * 60 * 60 * 24));
        return { days, hours, minutes, seconds };
      }

      updateDisplay({ days, hours, minutes, seconds }) {
        this.days.querySelector(".number").textContent = days;
        this.hrs.querySelector(".number").textContent = hours;
        this.mins.querySelector(".number").textContent = String(minutes).padStart(2, "0");
        this.secs.querySelector(".number").textContent = String(seconds).padStart(2, "0");

        // Reset visibility each tick (important so they can re-appear after rollover)
        this.days.style.display = "";
        this.hrs.style.display = "";
        this.mins.style.display = "";

        if (days <= 0) this.days.style.display = "none";
        if (days <= 0 && hours <= 0) this.hrs.style.display = "none";
        if (days <= 0 && hours <= 0 && minutes <= 0) this.mins.style.display = "none";
      }

      // ===== Weekly end-date logic =====

      computeEndDate() {
        const tz = "America/New_York";
        const now = new Date();

        // Get today's date/day-of-week in the desired timezone
        const local = this.getZonedParts(now, tz);
        const weekday = local.weekday; // Sun=0, Mon=1, Tue=2 ... Fri=5, Sat=6
        const year = String(local.year);
        const month = String(local.month).padStart(2, '0');
        const day = String(local.day).padStart(2, '0');
        
        const exceptionMondays = new Set([
          "2026-05-25",
          "2026-09-07"
        ]);

        const formattedDate = `${year}-${month}-${day}`;
        const isExceptionMonday = exceptionMondays.has(formattedDate);

        let target;

        // Saturday
        // Count down to Saturday at 11:59:59 PM
        if (weekday === 6) {
          target = {
            year: local.year,
            month: local.month,
            day: local.day
          };
        }

        // Sunday
        // Count down to Sunday at 11:59:59 PM
        else if (weekday === 0) {
          target = {
            year: local.year,
            month: local.month,
            day: local.day
          };
        }

        else if (weekday === 1 && isExceptionMonday) {
          target = {
            year: local.year,
            month: local.month,
            day: local.day
          };
        }

        // Monday through Friday
        // Count down to this week's Friday at 11:59:59 PM
        else {
          const daysUntilFriday = 5 - weekday;

          target = this.addDaysCivil(
            local.year,
            local.month,
            local.day,
            daysUntilFriday
          );
        }

        return this.zonedTimeToDate(
          {
            year: target.year,
            month: target.month,
            day: target.day,
            hour: 23,
            minute: 59,
            second: 59
          },
          tz
        );
      }

      // ===== Timezone helpers (no external libs) =====

      getZonedParts(date, timeZone) {
        const dtf = new Intl.DateTimeFormat("en-US", {
          timeZone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hourCycle: "h23",
          weekday: "short",
        });

        const parts = dtf.formatToParts(date);
        const map = Object.create(null);
        for (const p of parts) map[p.type] = p.value;

        const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

        return {
          year: Number(map.year),
          month: Number(map.month),
          day: Number(map.day),
          hour: Number(map.hour),
          minute: Number(map.minute),
          second: Number(map.second),
          weekday: weekdayMap[map.weekday],
        };
      }

      zonedTimeToDate({ year, month, day, hour, minute, second }, timeZone) {
        // UTC guess with same components
        const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

        // What does that guess look like in the target timezone?
        const got = this.getZonedParts(utcGuess, timeZone);

        const desiredUTC = Date.UTC(year, month - 1, day, hour, minute, second);
        const gotUTC = Date.UTC(got.year, got.month - 1, got.day, got.hour, got.minute, got.second);

        // Adjust to align the zoned wall time
        return new Date(utcGuess.getTime() + (desiredUTC - gotUTC));
      }

      addDaysCivil(year, month, day, addDays) {
        // Use UTC noon to avoid DST edges while rolling dates
        const d = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
        d.setUTCDate(d.getUTCDate() + addDays);
        return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
      }

      toYMD(year, month, day) {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      }
    }
  );
}
